'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import NotFoundFooter from './not-found-footer'

interface NotFoundPageProps {
  isAuthenticated?: boolean
  title?: string
  description?: string
  supportLinkText?: string
  supportLinkHref?: string
  backHref?: string
  backLabel?: string
}

export default function NotFoundPage({
  title = 'Page Not Found',
  description = 'If this space exists, check your account and access permissions, or contact the owner or',
  supportLinkText = 'Ababil Support',
  supportLinkHref = 'https://help.myababil.com/contact-us',
  backHref,
  backLabel = 'Back to Home',
}: NotFoundPageProps) {
  const router = useRouter()
  const { isAuthenticated } = useAuth()

  // Smart default for backHref based on authentication
  const smartBackHref = backHref || (isAuthenticated ? '/manage-spaces' : '/')

  return (
    <div className="flex min-h-screen flex-col bg-[#F5F6F8]">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      {/*
        320px: px-3, tiny logo (w-20), compact buttons (px-2 py-1.5 text-xs)
        375px: px-4, normal logo (w-28), normal buttons (px-3 text-sm)
        640px+: px-6, wider padding
        1024px+: px-12
      */}
      <header className="xs:py-5 w-full py-4 lg:py-8">
        <div className="xs:px-5 mx-auto flex w-full max-w-7xl items-center justify-between px-4 sm:px-8 md:px-10 lg:px-12">
          {/* Logo */}
          <Link href="/" className="shrink-0">
            <div className="xs:h-6 xs:w-28 relative h-5 w-20 lg:h-5.5 lg:w-[102.667px]">
              <Image
                src="https://web-assets.myababil.com/user-portal/images/welcome/Authentication-LogoHeader-Light.png"
                alt="Ababil Logo"
                fill
                sizes="(max-width: 374px) 80px, (max-width: 1023px) 112px, 102.667px"
                priority
              />
            </div>
          </Link>

          {/* Header Actions — hidden when user is already logged in */}
          {!isAuthenticated && (
            <nav className="xs:gap-2 flex shrink-0 items-center gap-3">
              <button
                type="button"
                onClick={() => router.push('/login')}
                className="xs:px-3 xs:py-2 xs:text-sm cursor-pointer rounded-lg border border-[#E5E7EB] bg-white px-2 py-1.5 text-xs leading-[150%] font-medium text-[#111928] not-italic shadow-[0_4px_8px_0_rgba(0,0,0,0.08)] transition-colors hover:bg-gray-100 sm:px-5 sm:py-2.5 sm:text-[14px]"
              >
                Login
              </button>
              <button
                type="button"
                onClick={() => router.push('/signup')}
                className="xs:px-3 xs:py-2 xs:text-sm cursor-pointer rounded-lg bg-[#5423FF] px-2 py-1.5 text-xs leading-[150%] font-medium whitespace-nowrap text-white not-italic shadow-[0_4px_7px_0_rgba(0,0,0,0.08)] transition-colors hover:bg-[#1a72e6] sm:px-5 sm:py-2.5 sm:text-[14px]"
              >
                Get started free
              </button>
            </nav>
          )}
        </div>
      </header>

      {/* ── Main Content ────────────────────────────────────────────────────── */}
      <main className="xs:py-10 flex flex-1 flex-col items-center justify-center px-4 py-8 text-center sm:px-6">
        {/* Illustration
            320px → 150px · 375px → 180px · 640px → 220px
        */}
        <div className="xs:h-[180px] xs:w-[180px] relative h-37.5 w-37.5 sm:h-55 sm:w-55">
          <Image
            src="https://web-assets.myababil.com/website/404-img.png"
            alt="404 Page Not Found"
            fill
            sizes="(max-width: 374px) 150px, (max-width: 639px) 180px, 220px"
            className="object-contain"
            priority
          />
        </div>

        {/* Title */}
        <h1 className="xs:text-[23px] my-3 text-center text-[32px] leading-[140%] font-bold text-[#0F172A] not-italic sm:text-[26px]">
          {title}
        </h1>

        {/* Description */}
        <p className="xs:mb-7 xs:text-sm mb-6 w-full max-w-sm px-1 text-center text-xs text-[16px] leading-[140%] font-normal text-[#64748B] not-italic sm:max-w-xl">
          {description}{' '}
          {supportLinkText && (
            <Link
              href={supportLinkHref}
              target="_blank"
              rel="noopener noreferrer"
              className="font-normal! text-[#5423FF]! underline!"
            >
              {supportLinkText}
            </Link>
          )}
          {description && supportLinkText && '.'}
        </p>

        {/* Back Button */}
        <Link
          href={smartBackHref}
          className="xs:px-6 xs:py-3 xs:text-sm inline-flex items-center rounded-lg bg-[#5423FF] px-5 py-2.5 text-xs font-medium text-white! transition-colors hover:bg-[#1a72e6]"
        >
          {backLabel}
        </Link>
      </main>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <NotFoundFooter marginClass="mt-auto" />
    </div>
  )
}
function useAuth(): { isAuthenticated: any } {
  throw new Error('Function not implemented.')
}
